import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  ScrollAreaAutosize,
  Select,
  Space,
  Tabs,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar, IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { truncateWithEllipsis } from "@utils/truncateText";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import {
  MAX_FILENAME_LENGTH_AFTER_UPLOAD,
  MAX_FILENAME_LENGTH_BEFORE_UPLOAD,
  MEGABYTE,
  ROLES,
} from "@constants/index";

// import { useDeletePrescriptionFile } from "@hooks/mutations/prescription/useDeletePrescriptionFile";
import { useUpdatePrescription } from "@hooks/mutations/prescription/useUpdatePrescription";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import { useAuth } from "@contexts/auth";

import { Prescription } from "@customTypes/prescription";

import { StyledEditModalContent } from "./EditModal.styled";


interface EditModalProps {
  prescription: Partial<Prescription>;
  readOnlyMode?: boolean;
  opened: boolean;
  close: () => void;
}

export const EditModal = ({
  prescription: initialPrescription,
  readOnlyMode = false,
  opened,
  close,
}: EditModalProps) => {
  const { t } = useTranslation();
  const { token, roles , can } = useAuth();
  const { patientId } = useParams();

  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [prescription, setPrescription] =
    useState<Partial<Prescription>>(initialPrescription);
  const [prescriptionDate, setPrescriptionDate] = useState<Date | null>(
    prescription?.prescriptionDate ?? null
  );
  // const [fileIndex, setFileIndex] = useState(-1);
  const [prescriptionFiles, setPrescriptionFiles] = useState<string[]>(
    prescription?.files as string[]
  );
  const [doctor, setDoctor] = useState<ComboboxItem | null>(null);

  const { mutate: updatePrescription, isLoading: isUpdatePrescriptionLoading } =
    useUpdatePrescription();
  // const {
  //   mutate: deletePrescriptionFile,
  //   isLoading: isDeletePrescriptionFileLoading,
  // } = useDeletePrescriptionFile();
  const {
    data: doctorsData,
    isLoading: isDoctorsLoading,
    isRefetching: isDoctorsRefetching,
  } = useGetUsers({
    permission: "prescriptions.create",
  });
  const doctorsComboboxItems = useMemo(() => {
    return doctorsData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [doctorsData]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleUpdatePrescription = () => {
    const jsFormData = new FormData();

    uploadedFiles.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append(`files`, file);
      });
    jsFormData.append(
      "prescription_date",
      prescriptionDate ? prescriptionDate.toISOString() : ""
    );
    jsFormData.append("prescribed_by", doctor?.value ?? "");

    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();
    updatePrescription(
      {
        formData: jsFormData,
        prescriptionId: prescription?.id ?? "",
        patientId: patientId ?? "",
      },
      {
        onSuccess: () => {
          close();
          setUploadedFiles([]);
        },
      }
    );
  };

  useEffect(() => {
    if (initialPrescription) {
      setPrescription(initialPrescription);
      // console.log(initialPrescription);
      setPrescriptionDate(initialPrescription.prescriptionDate ?? null);
      setPrescriptionFiles(initialPrescription.files as string[]);
      setDoctor({
        label: initialPrescription.prescribedBy?.fullName as string,
        value: initialPrescription.prescribedBy?.id as string,
      });
    }
  }, [initialPrescription]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title={
        <Group gap={7}>
          {readOnlyMode ? (
            <Text fw={500}>{t("view-prescription-for")}</Text>
          ) : (
            <Text fw={500}>{t("edit-prescription-for")}</Text>
          )}
          <Badge size="lg" bg="accent.0" c="accent.4">
            {prescription?.consultationInfo?.cId}
          </Badge>
        </Group>
      }
      styles={{ body: { minHeight: "auto" } }}>
      <StyledEditModalContent>
        <Tabs defaultValue="info">
          <Tabs.List grow>
            <Tabs.Tab value="info">{t("info")}</Tabs.Tab>
            <Tabs.Tab value="previous-files">{t("previous-files")}</Tabs.Tab>
            {readOnlyMode ? null : (
              <Tabs.Tab value="add-files">{t("add-files")}</Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="info">
            <form className="form">
              <div className="form-field">
                <DatePickerInput
                  label={t("prescription-date")}
                  withAsterisk
                  value={prescriptionDate}
                  disabled={readOnlyMode}
                  onChange={setPrescriptionDate}
                  leftSection={<IconCalendar size={16} stroke={1.5} />}
                />
              </div>
              <div className="form-field">
                <Select
                  label={t("doctor")}
                  placeholder={t("select-doctor")}
                  withAsterisk
                  disabled={!can("prescriptions.edit") || readOnlyMode}
                  data={doctorsComboboxItems || []}
                  value={doctor?.value ?? null}
                  onChange={(_val, option) => setDoctor(option)}
                  searchable
                  rightSection={
                    isDoctorsLoading || isDoctorsRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                />
              </div>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="previous-files" className="previous-files">
            <div className="files-wrapper">
              <div className="uploaded-files">
                <h4>{t("previously-uploaded-files")}</h4>
                <ScrollAreaAutosize h={200}>
                  {prescriptionFiles?.length ? (
                    <ul>
                      {prescriptionFiles.map((file, index) => (
                        <li key={index}>
                          <div className="content">
                            {(file as string).slice(-3) === "pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={"https://" + file + `?token=${token}`}>
                              {truncateWithEllipsis(
                                (file as string).split("/")[3],
                                MAX_FILENAME_LENGTH_AFTER_UPLOAD
                              )}
                            </a>
                          </div>

                          {/* {(file as string).includes(
                            AUTO_GENERATED_FILENAME
                          ) ? null : (
                            <Tooltip
                              label={t("delete-prescription-file")}
                              withArrow>
                              <ActionIcon
                                color="red"
                                loading={
                                  isDeletePrescriptionFileLoading &&
                                  fileIndex === index
                                }
                                variant="outline"
                                onClick={() => {
                                  setFileIndex(index);
                                  openDeleteModal({
                                    textNode: (
                                      <Text fz={"sm"}>
                                        {t("delete-prescription-file-message")}
                                      </Text>
                                    ),
                                    onConfirm: () => {
                                      if (prescription?.id) {
                                        deletePrescriptionFile(
                                          {
                                            prescriptionId: prescription.id,
                                            fileName: (file as string).split(
                                              "/"
                                            )[3],
                                          },
                                          {
                                            onSuccess: () =>
                                              setPrescriptionFiles(
                                                prescriptionFiles.filter(
                                                  (_, idx) => idx !== index
                                                )
                                              ),
                                          }
                                        );
                                      }
                                    },
                                  });
                                }}>
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )} */}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="empty-label">
                      {t("empty-previously-uploaded-files")}
                    </p>
                  )}
                </ScrollAreaAutosize>
              </div>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="add-files">
            <div className="files-wrapper">
              <div
                {...getRootProps({
                  className: `dropzone ${isDragActive ? "active" : ""} ${
                    isDragAccept ? "accept" : ""
                  } ${isDragReject ? "reject" : ""}`,
                })}>
                <input {...getInputProps()} />

                <div className="text-content">
                  <div className="svg-wrapper">
                    <LiaFileUploadSolid />
                  </div>
                  <div className="p-wrapper">
                    <p>{t("drag-file-here")}</p>
                    <p>{t("drag-file-here-continuation")}</p>
                  </div>
                </div>
              </div>
              <div className="uploaded-files">
                <h4>{t("uploaded-files")}</h4>
                {!uploadedFiles.length ? (
                  <Alert
                    mt={"md"}
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle />}>
                    <Text fz={14} lh={1.5} c="blue">
                      {t("files-appear-info")}
                    </Text>
                  </Alert>
                ) : (
                  <ScrollAreaAutosize h={200} type="auto">
                    <ul>
                      {uploadedFiles.map((file, index) => (
                        <li key={index}>
                          <div className="content">
                            {file.type === "application/pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <p>
                              {truncateWithEllipsis(
                                file.name,
                                MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                              )}{" "}
                              ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                            </p>
                          </div>

                          <ActionIcon
                            color="red"
                            variant="outline"
                            onClick={() => removeFile(file)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </li>
                      ))}
                    </ul>
                  </ScrollAreaAutosize>
                )}
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>

        {readOnlyMode ? (
          <Space h="lg" />
        ) : (
          <Group justify="end" mt="xl">
            <PermissionGate permission="prescriptions.edit"><Button
              disabled={!prescriptionDate || !doctor}
              onClick={handleUpdatePrescription}
              loading={isUpdatePrescriptionLoading}>
              {t("update-prescription")}
            </Button></PermissionGate>
          </Group>
        )}
      </StyledEditModalContent>
    </Modal>
  );
};
